import { computed, type ComputedRef } from "vue";

import { computeIntakeLabel } from "@/models/equipment/intake/properties";
import { computeMotorLabel } from "@/models/equipment/motors/properties";
import { computeSealLabel } from "@/models/equipment/seals/properties";
import { computeSensorLabel } from "@/models/equipment/sensors/properties";

import {
  buildEquipmentStringSegments,
  buildPumpEquipmentItems,
  extractMeasurements,
  getEquipmentSegmentAtMD,
  type BaseLengthEquipment,
  type EquipmentMeasurements,
  type EquipmentSegmentProperties,
  type LengthItem
} from "@/libs/calculations/equipmentString";
import {
  getCasingAtMD,
  getTubingAtMD,
  type CasingAtDepth,
  type TubingAtDepth
} from "@/libs/calculations/wellboreGeometry";
import {
  findMaxDlsAlongTraverse,
  interpolateSurveyAtMD,
  resampleSurveyAtInterval,
  type MaxDogLegSeverity,
  type SurveyPointAtMD
} from "@/libs/calculations/deviationSurvey";

import type { Scenario } from "@/composables/forms/formTypes";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";

export type UseWellboreGeometryOptions = {
  scenarioCalculations: ScenarioCalculations;
  scenario?: Scenario | null;
  /**
   * Survey rows as `[md, inclination, azimuth, tvd, dls]`. Pass a getter to
   * keep the rows reactive - a plain array is read once, at setup.
   */
  surveyRows: number[][] | (() => number[][]);
};

export type UseWellboreGeometryReturn = {
  equipmentSegments: ComputedRef<LengthItem[]>;
  maxDlsAboveLandingDepth: ComputedRef<MaxDogLegSeverity | null>;
  traverseTubingLengthFt: ComputedRef<number | null>;
  surveyAt: (measuredDepthFt: number) => SurveyPointAtMD | null;
  equipmentAt: (measuredDepthFt: number) => EquipmentSegmentProperties | null;
  casingAt: (measuredDepthFt: number) => CasingAtDepth | null;
  tubingAt: (measuredDepthFt: number) => TubingAtDepth | null;
  resampleSurvey: (
    intervalFt: number,
    endMeasuredDepthFt?: number
  ) => SurveyPointAtMD[];
};

/**
 * Shared wellbore + equipment string geometry.
 *
 * Pulls together the survey primitives in `libs/calculations/wellboreGeometry`
 * and the string layout in `libs/calculations/equipmentString`, so the bending
 * stress work (SPYG-88) has one entry point instead of reaching into the chart
 * composables.
 *
 * Everything here is derived state - no mutations, nothing awaited.
 */
export function useWellboreGeometry(
  options: UseWellboreGeometryOptions
): UseWellboreGeometryReturn {
  const { scenarioCalculations, scenario } = options;

  const surveyRows = computed(() =>
    typeof options.surveyRows === "function"
      ? options.surveyRows() ?? []
      : options.surveyRows ?? []
  );

  const tubingLengthFt = computed(
    () => scenarioCalculations.equipmentLengths.tubingLengthFt.value
  );

  const wellProperties = computed(() => scenario?.well_properties ?? null);

  const equipmentSegments = computed<LengthItem[]>(() => {
    if (!scenario?.equipment) return [];

    const intakes = scenarioCalculations.orderedIntakes.value
      .filter(v => !!v.r_intake)
      .map(v => ({
        ...v,
        ref: v.r_intake,
        ...extractMeasurements(v.r_intake as EquipmentMeasurements),
        display_label: computeIntakeLabel(v.r_intake, v.use_high_strength_shaft)
      })) as BaseLengthEquipment[];

    const seals = scenarioCalculations.descendingOrderSeals.value
      .filter(v => !!v.r_seal)
      .map(v => ({
        ...v,
        ref: v.r_seal,
        ...extractMeasurements(v.r_seal as EquipmentMeasurements),
        display_label: computeSealLabel(v.r_seal, v.use_high_strength_shaft)
      })) as BaseLengthEquipment[];

    const motors = (
      scenario.equipment.r_motor
        ? [
            {
              ref: scenario.equipment.r_motor,
              ...extractMeasurements(
                scenario.equipment.r_motor as EquipmentMeasurements
              ),
              display_label: computeMotorLabel(scenario.equipment.r_motor)
            }
          ]
        : []
    ) as BaseLengthEquipment[];

    const sensors = (
      scenario.equipment.r_sensor
        ? [
            {
              ref: scenario.equipment.r_sensor,
              ...extractMeasurements(
                scenario.equipment.r_sensor as EquipmentMeasurements
              ),
              display_label: computeSensorLabel(scenario.equipment.r_sensor)
            }
          ]
        : []
    ) as BaseLengthEquipment[];

    return buildEquipmentStringSegments({
      tubingLengthFt: tubingLengthFt.value,
      pumps: buildPumpEquipmentItems(
        scenarioCalculations.descendingOrderPumps.value
      ),
      intakes,
      seals,
      motors,
      sensors
    });
  });

  const maxDlsAboveLandingDepth = computed(() =>
    findMaxDlsAlongTraverse(surveyRows.value, tubingLengthFt.value)
  );

  /**
   * `Final_Traverse!C5 = VLOOKUP(S5,BN4:BO464,2,)-E4+E23/12/2`. The string
   * sits so that the point half a motor length above its bottom is on the
   * worst station. `E4` sums the modelled segments, the same items that the
   * bending stress model receives, so it leaves out the bolt-on discharge.
   * `null` when no station is above the landing depth, or when the string
   * would start at or above the surface.
   */
  const traverseTubingLengthFt = computed(() => {
    const worst = maxDlsAboveLandingDepth.value;
    if (!worst) return null;
    const segments = equipmentSegments.value;
    const equipmentLengthFt = segments.reduce(
      (sum, segment) => sum + segment.equipmentLengthFt,
      0
    );
    const motorLengthFt =
      segments.find(segment => segment.type === "motor")?.equipmentLengthFt ??
      0;
    const lengthFt =
      worst.measuredDepthFt - equipmentLengthFt + motorLengthFt / 2;
    return lengthFt > 0 ? lengthFt : null;
  });

  const casingAt = (measuredDepthFt: number) =>
    getCasingAtMD(
      measuredDepthFt,
      wellProperties.value,
      scenarioCalculations.equipmentLengths.totalCasingLengthFt.value
    );

  const tubingAt = (measuredDepthFt: number) =>
    getTubingAtMD(measuredDepthFt, wellProperties.value, tubingLengthFt.value);

  return {
    equipmentSegments,
    maxDlsAboveLandingDepth,
    traverseTubingLengthFt,
    casingAt,
    tubingAt,
    surveyAt: (measuredDepthFt: number) =>
      interpolateSurveyAtMD(measuredDepthFt, surveyRows.value),
    equipmentAt: (measuredDepthFt: number) =>
      getEquipmentSegmentAtMD(measuredDepthFt, equipmentSegments.value),
    resampleSurvey: (intervalFt: number, endMeasuredDepthFt?: number) =>
      resampleSurveyAtInterval(surveyRows.value, intervalFt, endMeasuredDepthFt)
  };
}
